import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Sunset, Sparkles, Moon } from 'lucide-react';

export interface MoodPreset {
  id: string;
  name: string;
  icon: React.ReactNode;
  devicesInUse: number;
}

export interface MoodPresetsProps {
  presets?: MoodPreset[];
  activePreset: string | null;
  onSelect: (presetId: string) => void;
  className?: string;
}

const defaultPresets: MoodPreset[] = [
  { id: 'morning', name: 'Morning', icon: <Sun className="w-14 h-14" />, devicesInUse: 4 },
  { id: 'evening', name: 'Evening', icon: <Sunset className="w-14 h-14" />, devicesInUse: 6 },
  { id: 'relax', name: 'Relax', icon: <Sparkles className="w-14 h-14" />, devicesInUse: 3 },
  { id: 'night', name: 'Night', icon: <Moon className="w-14 h-14" />, devicesInUse: 2 },
];

export const MoodPresets: React.FC<MoodPresetsProps> = ({
  presets = defaultPresets,
  activePreset,
  onSelect,
  className = '',
}) => {
  return (
    <div className={`flex gap-4 ${className}`}>
      {presets.map((preset) => {
        const isActive = activePreset === preset.id;

        return (
          <motion.button
            key={preset.id}
            onClick={() => onSelect(preset.id)}
            className={`
              relative flex-1 min-w-[160px] bg-white rounded-2xl p-6 text-center
              shadow-[0_2px_8px_rgba(0,0,0,0.08)]
              hover:shadow-[0_4px_16px_rgba(0,0,0,0.12)]
              transition-shadow duration-200
              focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A054]
              ${isActive ? 'ring-2 ring-[#D4A054] bg-[#FDF6E9]' : ''}
            `}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            animate={isActive ? { scale: 1.02 } : { scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            {/* Icon */}
            <motion.div
              className="text-[#D4A054] mb-3 flex justify-center"
              animate={isActive ? { scale: 1.1 } : { scale: 1 }}
              transition={{ duration: 0.2 }}
            >
              {preset.icon}
            </motion.div>

            {/* Label */}
            <h3 className="text-[#333333] text-base font-medium mb-1">
              {preset.name}
            </h3>

            {/* Subtitle */}
            <p className="text-[#666666] text-xs">
              {preset.devicesInUse} Devices in use
            </p>
          </motion.button>
        );
      })}
    </div>
  );
};

export default MoodPresets;
